import React, { useCallback, useEffect, useState } from 'react';
import { pick, prop } from 'ramda';
import { useTranslations } from '@core/js/hooks/i18n';
import { useDeviceSelector, usePageSelector, useUserSelector } from '@core/js/store/hooks';
import { Link } from '@theme/js/components/common/Link';
import { getURLParam } from '@theme/js/utils/url.util';
import { storageService } from '@core/js/services/storage.service';

import { IS_USER_SIGN_IN_STORAGE_KEY } from '@core/js/components/main/SignInNotificationBanner/constants';
import { LAYER_POSITION } from 'js/components/system/Router/constants'; /* Lugano changes */

import style from './SignInNotificationBanner.scss';

export const SignInNotificationBanner: React.FC<{
    setIsVisibleNotification?: (isVisible?: boolean) => void;
    cookiePosition?: LAYER_POSITION;
    aboveHeaderWcID?: any;
    notificationRef?: any;
}> = ({
    setIsVisibleNotification, /* Lugano changes */
    cookiePosition, /* Lugano changes */
    aboveHeaderWcID, /* Lugano changes */
    notificationRef /* Lugano changes */
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const isMobile = useDeviceSelector(prop('isMobile')); /* Lugano changes */
    const { headerWcId, overrideHeader } = usePageSelector(pick(['headerWcId', 'overrideHeader'])); /* Lugano changes */
    const translate = useTranslations('playtech.mobile.system.message.signInNotification.');
    const message = translate('message');
    const [messagePart1, messagePart2] = message.split('{0}');
    const layerClass = cookiePosition === LAYER_POSITION.TOP || aboveHeaderWcID
            ? style.cookieIsTop : style.cookieIsBottom; /* Lugano changes */
    const hideHeaderByUrl = getURLParam(location.search, 'hideHeader') === '1'; /* Lugano changes */
    const hideDesktopHeaderByUrl = getURLParam(location.search, 'showHeaderOnlyOnMobile') === '1'; /* Lugano changes */
    const hideHeader = hideHeaderByUrl || hideDesktopHeaderByUrl && !isMobile || overrideHeader && !headerWcId; /* Lugano changes */

    useEffect(() => {
        if (isLoggedIn) {
            storageService.local.setItem(IS_USER_SIGN_IN_STORAGE_KEY, true);

            setIsVisible(false);
            setIsVisibleNotification && setIsVisibleNotification(false); /* Lugano changes */
        } else {
            storageService.local.removeItem(IS_USER_SIGN_IN_STORAGE_KEY);
        }
    }, [isLoggedIn, setIsVisibleNotification]);

    useEffect(() => {
        const onStorageChange = (event: StorageEvent): void => {
            const { key, newValue } = event;

            if (key !== IS_USER_SIGN_IN_STORAGE_KEY) {
                return;
            }

            setIsVisible(Boolean(newValue && !isLoggedIn));
            setIsVisibleNotification && setIsVisibleNotification(Boolean(newValue && !isLoggedIn)); /* Lugano changes */
        };

        window.addEventListener('storage', onStorageChange);

        return () => {
            window.removeEventListener('storage', onStorageChange);
        };
    }, [isLoggedIn, setIsVisibleNotification]);

    const reload = useCallback((event: React.MouseEvent<HTMLAnchorElement>): void => {
        event.preventDefault();
        window.location.reload();
    }, []);

    if (!isVisible) {
        return null;
    }

    return (
        <div
                className={`${style.container} ${hideHeader ? style.withOutHeader : ''} ${layerClass} cms-sign-in-notification-banner`} /* Lugano changes */
                {...(isVisible && notificationRef && !aboveHeaderWcID && { ref: notificationRef })} /* Lugano changes */
        >
            <span>
                {messagePart1}
                <Link href="#" className={style.link} onClick={reload}>
                    {translate('linkText')}
                </Link>
                {messagePart2}
            </span>
        </div>
    );
};
